import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authReturnPath } from '../utils/authReturn';
import { motion } from 'framer-motion';
import { FiUser, FiCalendar, FiMapPin, FiCheck } from 'react-icons/fi';
import supabase from '../services/supabase';
import { networkCountry, countryName } from '../services/visitorCountry';
import '../styles/ProfileSetup.scss';
import { track } from '../services/track';
import { EV } from '../shared/analyticsEvents';

const ProfileSetup = () => {
  useEffect(() => { track(EV.PROFILE_SETUP_VIEWED); }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  
  // Basic info
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  
  // Demographics (optional)
  const [birthday, setBirthday] = useState('');
  const [gender, setGender] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');

  // Starts filled in with where they are; theirs to change.
  useEffect(() => {
    let cancelled = false;
    networkCountry().then((iso2) => {
      const name = iso2 ? countryName(iso2) : null;
      if (!cancelled && name) setCountry((current) => current || name);
    });
    return () => { cancelled = true; };
  }, []);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleNext = () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name');
      return;
    }
    setError(null);
    setCurrentStep(2);
  };

  const handleSkipDemographics = async () => {
    await handleComplete(true);
  };

  const handleComplete = async (skipDemographics = false) => {
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // First, get existing user data to preserve phone_number and country_code
      const { data: existingUser, error: fetchError } = await supabase
        .from('users')
        .select('phone_number, country_code, username, email')
        .eq('id', user.id)
        .single();

      if (fetchError) {
        console.error('Error fetching existing user:', fetchError);
      }

      // Prepare profile data
      const profileData = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        display_name: `${firstName.trim()} ${lastName.trim()}`,
        
        // Preserve existing data
        phone_number: existingUser?.phone_number || null,
        country_code: existingUser?.country_code || null,
        username: existingUser?.username || null,
        email: existingUser?.email || null,
        
        // Demographics
        birthday: skipDemographics ? null : (birthday || null),
        gender: skipDemographics ? null : (gender || null),
        city: skipDemographics ? null : (city.trim() || null),
        state: skipDemographics ? null : (state.trim() || null),
        
        // Country only when they gave one. Skipping, or leaving it empty,
        // keeps the one recorded at sign-in from where they are
        // (services/auth.js) instead of wiping it.
        ...(!skipDemographics && country.trim() ? { country: country.trim() } : {}),

        // Timestamps
        signup_completed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      console.log('📝 Updating profile with data:', profileData);

      // Update user profile
      const { data: updatedData, error: updateError } = await supabase
        .from('users')
        .update(profileData)
        .eq('id', user.id)
        .select()
        .single();

      if (updateError) throw updateError;

      console.log('✅ Profile updated successfully:', updatedData);
      track(EV.PROFILE_SETUP_COMPLETED);

      // Back to what they were doing when they signed up (an invitation
      // mid-draft), otherwise the dashboard.
      const redirectTo = authReturnPath(location);
      navigate(redirectTo || '/dashboard', { replace: !!redirectTo });
      
    } catch (error) {
      console.error('Error completing profile:', error);
      setError(error.message || 'Failed to create profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-setup-container">
      <div className="profile-setup-background">
        <img src="/images/events/4.jpg" alt="Background" />
        <div className="profile-setup-overlay"></div>
      </div>

      <div className="profile-setup-content">
        <motion.div
          className="profile-setup-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="profile-setup-title">
            {currentStep === 1 ? 'Complete Your Profile' : 'Help Us Personalize'}
          </h1>
          <p className="profile-setup-subtitle">
            {currentStep === 1 
              ? 'Tell us a bit about yourself'
              : 'Share more details (all optional)'}
          </p>

          {/* Step Indicator */}
          <div className="step-indicator">
            <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
              <FiUser />
            </div>
            <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
              <FiCalendar />
            </div>
          </div>

          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="input-group">
                <label>First Name *</label>
                <input
                  type="text"
                  placeholder="Enter your first name"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    setError(null);
                  }}
                  autoFocus
                />
              </div>

              <div className="input-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  placeholder="Enter your last name"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    setError(null);
                  }}
                />
              </div>

              {error && <p className="error-text">{error}</p>}

              <button 
                className="btn btn-primary" 
                onClick={handleNext}
                disabled={!firstName.trim() || !lastName.trim()}
              >
                Next
              </button>
            </motion.div>
          )}

          {/* Step 2: Demographics (Optional) */}
          {currentStep === 2 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="input-group">
                <label>Birthday (Optional)</label>
                <div className="input-with-icon">
                  <FiCalendar className="input-icon" />
                  <input
                    type="date"
                    value={birthday}
                    onChange={(e) => setBirthday(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>

              <div className="input-group">
                <label>Gender (Optional)</label>
                <select 
                  value={gender} 
                  onChange={(e) => setGender(e.target.value)}
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="non-binary">Non-binary</option>
                  <option value="prefer-not-to-say">Prefer not to say</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="input-group">
                <label>Location (Optional)</label>
                <div className="input-with-icon">
                  <FiMapPin className="input-icon" />
                  <input
                    type="text"
                    placeholder="City"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                </div>
              </div>

              <div className="input-row">
                <input
                  type="text"
                  placeholder="State/Region"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                />
              </div>

              {error && <p className="error-text">{error}</p>}

              <button 
                className="btn btn-primary" 
                onClick={() => handleComplete(false)}
                disabled={loading}
              >
                {loading ? (
                  'Completing...'
                ) : (
                  <>
                    <FiCheck />
                    Complete Profile
                  </>
                )}
              </button>

              <button 
                className="btn btn-ghost mt-md" 
                onClick={handleSkipDemographics}
                disabled={loading}
              >
                Skip for now
              </button>

              <p className="privacy-note">
                Your demographic information helps us understand our community better and is used only for analytics. It will never be shared publicly.
              </p>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ProfileSetup;